import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { ChevronRight } from 'lucide-react';
import { WarningIcon } from '@phosphor-icons/react';
import { cn, objectToQueryParams } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ChapterwiseTests from './ChapterwiseTests';
import NonChapterwiseTests from './NonChapterwiseTests';
import TestListingLoader from '../../components/loaders/TestListingLoader';
import { AppLayout } from '../../Layout/AppLayout';
import { usePackStorage } from '../../Layout/usePackStorage';

const initialChapterFilters = {
  search: '',
  testStatus: 'All',
  sorting: null,
  selectUnit: null,
  testType: null,
  page: 1,
  limit: 15,
  isFilterApplied: false,
};

/** Same drift threshold as the AYJR system health check page (5 minutes). */
const SYSTEM_CLOCK_DRIFT_MS = 300_000;

function getTimezoneCheckUrl() {
  const base =
    typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL
      ? String(import.meta.env.VITE_API_URL).replace(/\/$/, '')
      : '';
  return base
    ? `${base}/api/timezone/?timeZone=Asia/Kolkata`
    : 'https://api.quizrr.in/api/timezone/?timeZone=Asia/Kolkata';
}

/** Mirrors the fallback in the static health-check page when the API is unreachable. */
function getFallbackClockWarningMessage() {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const ist = new Date(utc + 5.5 * 3600000);
  const diff = Math.abs(ist - now);
  if (diff <= 600000) return null;
  return 'Your system clock may be incorrect. Please set your device to Indian Standard Time (IST) so tests start and end at the correct times.';
}

export default function UnlockedTestListing() {
  const navigate = useNavigate();
  const hasSyncedDefaultPage = useRef(false);
  const {
    purchasedPacks,
    selectedPack,
    handlePackChange,
    isLoading: packLoading,
    updateURLIfPackMismatch,
  } = usePackStorage();

  const { packId: urlPackId } = useParams();
  const location = useLocation();

  // Ref for the main scrollable container
  const mainContentRef = useRef(null);

  // Use packId from URL or selected pack
  const packId = useMemo(() => urlPackId || selectedPack?._id, [urlPackId, selectedPack?._id]);

  // Separate state for static data (categories, filters) vs dynamic data (tests)
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [availableFilters, setAvailableFilters] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);

  // Dynamic test data state
  const [tests, setTests] = useState([]);
  const [chapterWiseTests, setChapterWiseTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testLoading, setTestLoading] = useState(false); // Separate loading for tests only
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [banners, setBanners] = useState(null);
  const [isPackPurchased, setIsPackPurchased] = useState(true);
  const [systemClockWarning, setSystemClockWarning] = useState(null);

  // Filter state
  const [chapterFilters, setChapterFilters] = useState(initialChapterFilters);
  const [filterLoading, setFilterLoading] = useState(false);

  // Mobile sidebar state
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Memoized computed values
  const hasChapterWiseCategories = useMemo(
    () => categories.some((cat) => cat.parentTitle && cat.parentTitle.toLowerCase().includes('chapter')),
    [categories],
  );

  const selectedCategoryData = useMemo(
    () => categories.find((category) => category.groupId === selectedCategory),
    [categories, selectedCategory],
  );

  const isPYQCategory = useMemo(
    () => selectedCategoryData?.parentTitle.toLowerCase().includes('pyq'),
    [selectedCategoryData],
  );

  const isChapterWiseCategory = useMemo(
    () => selectedCategoryData?.parentTitle.toLowerCase().includes('chapter'),
    [selectedCategoryData],
  );

  // Function to scroll to top
  const scrollToTop = useCallback(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, []);

  const getParentCategories = useMemo(() => {
    const parentCats = {};
    categories.forEach((cat) => {
      if (!parentCats[cat.parentTitle]) {
        parentCats[cat.parentTitle] = {
          title: cat.parentTitle,
          groups: [],
        };
      }
      parentCats[cat.parentTitle].groups.push(cat);
    });
    return Object.values(parentCats);
  }, [categories]);

  // Update URL pack if different from selected pack
  useEffect(() => {
    if (urlPackId && selectedPack && urlPackId !== selectedPack._id) {
      updateURLIfPackMismatch(urlPackId);
    }
  }, [urlPackId, selectedPack?._id]);

  useEffect(() => {
    let cancelled = false;

    async function checkSystemClock() {
      const url = getTimezoneCheckUrl();
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 8000);

      try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error('timezone request failed');
        const data = await res.json();
        const serverTime = new Date(data.currentTime);
        const localTime = new Date();
        const drift = Math.abs(serverTime - localTime);
        if (cancelled) return;
        if (drift > SYSTEM_CLOCK_DRIFT_MS) {
          const offBy = Math.round(drift / 60000);
          setSystemClockWarning(
            `Your device clock is off by about ${offBy} minute${offBy === 1 ? '' : 's'} compared to Indian Standard Time. Please correct your date and time settings.`,
          );
        }
      } catch {
        if (cancelled) return;
        const fallback = getFallbackClockWarningMessage();
        if (fallback) setSystemClockWarning(fallback);
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    checkSystemClock();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pack details and categories fetch (only when packId changes)
  useEffect(() => {
    const fetchPackDetails = async () => {
      if (!packId || packLoading) return;
      setLoading(true);
      try {
        const response = await axios.get(`/api/packs/v2/dashboard/${packId}/basic`);
        let fetchedCategories = response.data.data.category.flatMap((c) =>
          c.groups.map((g) => ({ ...g, parentTitle: c.title })),
        );
        if (!response?.data?.data?.isPackPurchased) {
          fetchedCategories = [
            ...fetchedCategories.filter((c) => c.isFreemium),
            ...fetchedCategories.filter((c) => !c.isFreemium),
          ];
          setCategories(fetchedCategories);
        } else {
          setCategories(fetchedCategories);
        }
        setBanners(response.data.data?.banners);

        const urlParams = new URLSearchParams(location.search);
        let categoryIdFromUrl = urlParams.get('categoryId');
        const categoryIdFromState = location.state?.selectedCategoryId;

        let targetCategoryId = categoryIdFromState || categoryIdFromUrl;

        if (targetCategoryId && fetchedCategories.length > 0) {
          let targetCategory = fetchedCategories.find((cat) => cat.groupId === targetCategoryId);
          if (!targetCategory) {
            targetCategory = fetchedCategories.find(
              (cat) => cat.title.toLowerCase() === targetCategoryId.toLowerCase(),
            );
          }
          if (targetCategory) {
            targetCategoryId = targetCategory.groupId;
          }
        }

        if (targetCategoryId && fetchedCategories.length > 0) {
          const targetCategory = fetchedCategories.find((cat) => cat.groupId === targetCategoryId);
          if (targetCategory) {
            setSelectedCategory(targetCategory.groupId);
          } else {
            setSelectedCategory(fetchedCategories[0].groupId);
          }
        } else {
          setSelectedCategory(fetchedCategories[0].groupId);
        }

        setError(null);
      } catch (err) {
        setError('Failed to fetch pack details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchPackDetails();
  }, [packId, packLoading, location.search, location.state]);

  // Reset filters and scroll to top when category changes
  useEffect(() => {
    if (selectedCategory) {
      setChapterFilters({
        ...initialChapterFilters,
        groupId: selectedCategory,
      });
      scrollToTop();
      hasSyncedDefaultPage.current = false;
    }
  }, [selectedCategory, scrollToTop]);

  // Fetch filters
  useEffect(() => {
    const fetchFilters = async () => {
      if (!selectedCategory || !packId) return;

      setFilterLoading(true);
      try {
        const response = await axios.get(`/api/packs/v2/dashboard/${packId}/${selectedCategory}/filters`);
        setAvailableFilters(response.data.data);
      } catch (err) {
        setAvailableFilters(null);
      } finally {
        setFilterLoading(false);
      }
    };
    fetchFilters();
  }, [packId, selectedCategory]);

  // Fetch tests (chapterwise / normal)
  const fetchChapterWiseTests = useCallback(async () => {
    if (!packId || !selectedCategory) return;
    setTestLoading(true);
    try {
      const filtersString = objectToQueryParams(chapterFilters);
      const response = await axios.get(
        `/api/packs/v2/dashboard/${packId}/${selectedCategory}/tests?${filtersString}&chapterWise=true`,
      );
      const responseData = response.data.data;

      // Only sync defaultPage on first load for this category
      if (
        !hasSyncedDefaultPage.current &&
        responseData?.defaultPage &&
        responseData.defaultPage !== chapterFilters.page
      ) {
        setChapterFilters((prev) => ({
          ...prev,
          page: responseData.defaultPage,
        }));
        setCurrentPage(responseData.defaultPage);
        hasSyncedDefaultPage.current = true; // Mark as synced
      }
      setChapterWiseTests(responseData.subgroups || []);
      setTests(responseData.subgroups || []);
      setTotalCount(responseData.count || 0);
      setTotalPages(Math.ceil((responseData.count || 0) / chapterFilters.limit));
      setError(null);
    } catch (err) {
      setError('Failed to fetch tests. Please try again later.');
      setTests([]);
      setChapterWiseTests([]);
    } finally {
      setTestLoading(false);
    }
  }, [packId, selectedCategory, chapterFilters]);

  const fetchNormalTests = useCallback(async () => {
    if (!packId || !selectedCategory) return;
    setTestLoading(true);
    try {
      const filtersString = objectToQueryParams(chapterFilters);
      const response = await axios.get(`/api/packs/v2/dashboard/${packId}/${selectedCategory}/tests?${filtersString}`);
      if (!response?.data?.data?.isPackPurchased) {
        setIsPackPurchased(false);
      }
      const responseData = response.data.data;

      // Only sync defaultPage on first load for this category
      if (
        !hasSyncedDefaultPage.current &&
        responseData?.defaultPage &&
        responseData.defaultPage !== chapterFilters.page
      ) {
        setChapterFilters((prev) => ({
          ...prev,
          page: responseData.defaultPage,
        }));
        setCurrentPage(responseData.defaultPage);
        hasSyncedDefaultPage.current = true; // Mark as synced
      }
      setTests(responseData.subgroups || []);
      setTotalPages(Math.ceil(responseData.count / chapterFilters.limit));
      setError(null);
    } catch (err) {
      setError('Failed to fetch tests. Please try again later.');
      setTests([]);
    } finally {
      setTestLoading(false);
    }
  }, [packId, selectedCategory, chapterFilters]);

  useEffect(() => {
    if (!selectedCategory || !packId || categories.length === 0) return;
    // Wait until category-reset effect syncs chapterFilters.groupId so we don't
    // fire once on selectedCategory and again when filters are reset.
    if (chapterFilters.groupId !== selectedCategory) return;
    if (isChapterWiseCategory) {
      fetchChapterWiseTests();
    } else {
      fetchNormalTests();
    }
  }, [
    packId,
    selectedCategory,
    chapterFilters,
    isChapterWiseCategory,
    fetchChapterWiseTests,
    fetchNormalTests,
    categories.length,
  ]);

  const handlePageChange = useCallback(
    (page) => {
      if (page >= 1 && page <= totalPages && page !== chapterFilters.page) {
        setChapterFilters((prev) => ({ ...prev, page }));
        setCurrentPage(page);
      }
    },
    [totalPages, chapterFilters.page],
  );

  const handleChapterFilterChange = useCallback((filterUpdates) => {
    setChapterFilters((prev) => ({
      ...prev,
      isFilterApplied: true,
      ...filterUpdates,
    }));
  }, []);

  const handleClearChapterFilters = useCallback(() => {
    setChapterFilters({
      ...initialChapterFilters,
      testStatus: 'All',
      groupId: selectedCategory,
    });
  }, [selectedCategory]);

  const handleCategoryChange = useCallback((categoryId) => {
    setSelectedCategory(categoryId);
    setMobileSidebarOpen(false);
  }, []);

  const handleSubjectChange = useCallback((subject) => {
    setSelectedSubject(subject);
    setSelectedCategory(subject.groupId);
    setMobileSidebarOpen(false);
  }, []);

  // Mobile sidebar
  const MobileSidebar = () => (
    <div className="xl:hidden fixed inset-0 z-50 bg-black/20 ">
      <div className="absolute left-0 top-0 h-full w-80 bg-[var(--q3-surface-dim)] overflow-y-auto">
        <div className="p-4">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-[var(--q3-neutral-default)]">All Tests</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileSidebarOpen(false)}
              className="text-[var(--q3-neutral-default)]"
            >
              ✕
            </Button>
          </div>
          <div className="space-y-6">
            {Object.entries(
              categories.reduce((acc, cat) => {
                (acc[cat.parentTitle] = acc[cat.parentTitle] || []).push(cat);
                return acc;
              }, {}),
            ).map(([parentTitle, subCategories]) => (
              <div key={parentTitle}>
                <div className="flex items-center gap-2 px-2 mb-3">
                  <p className="text-xs text-[var(--q3-neutral-light)] font-medium tracking-wider whitespace-nowrap">
                    {parentTitle.toUpperCase()}
                  </p>
                  <div className="flex-1 border-t border-dashed border-[var(--q3-stroke-normal)]"></div>
                </div>
                <div className="space-y-2">
                  {subCategories.map((category) => (
                    <div
                      key={category.groupId}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors duration-200',
                        selectedCategory === category.groupId
                          ? 'bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)]'
                          : 'hover:bg-[var(--q3-surface-dim)] text-[var(--q3-neutral-default)]',
                      )}
                      onClick={() => handleCategoryChange(category.groupId)}
                    >
                      <div className="flex-1">
                        <span className="text-sm font-medium">{category.shortTitle || category.title}</span>
                      </div>
                      {category.isFreemium && purchasedPacks?.length === 0 && (
                        <Badge className="text-[var(--q3-neutral-default_inverted)] bg-[var(--q3-danger-normal)]">
                          Free
                        </Badge>
                      )}
                      {category?.tagText?.trim()?.length > 0 && (
                        <Badge
                          className="text-[var(--q3-neutral-default_inverted)]"
                          style={
                            category.tagColor
                              ? { backgroundColor: category.tagColor }
                              : { backgroundColor: 'var(--q3-danger-normal)' }
                          }
                        >
                          {category?.tagText}
                        </Badge>
                      )}
                      <ChevronRight className="h-4 w-4 text-[var(--q3-accent-normal)]" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
      <div className="flex-1 h-screen overflow-y-scroll" ref={mainContentRef}>
        <div className="w-full px-4 flex flex-col items-center">
          {systemClockWarning && (
            <div
              role="alert"
              className="mx-4 mt-4 w-full max-w-[1010px] rounded-lg border border-[var(--q3-danger-normal)] bg-[var(--q3-surface-default)] px-4 py-3 text-sm text-[var(--q3-neutral-default)] shadow-sm"
            >
              <div className="flex gap-3 items-start">
                <WarningIcon
                  className="flex-shrink-0 mt-0.5 text-[var(--q3-danger-normal)]"
                  size={22}
                  weight="fill"
                  aria-hidden
                />
                <p className="leading-relaxed">{systemClockWarning}</p>
              </div>
            </div>
          )}
          <div className="w-full w-max-[840px] mt-8 flex flex-col xl:flex-row gap-6 justify-center">
            {/* Mobile sidebar trigger */}
            <div className="xl:hidden w-full px-4 mb-4">
              <Button
                variant="outline"
                className="w-full justify-between text-[var(--q3-neutral-default)] bg-[var(--q3-surface-dim)] border-[var(--q3-stroke-normal)]"
                onClick={() => setMobileSidebarOpen(true)}
              >
                <span>Select Test Category</span>
                <ChevronRight className="h-4 w-4 text-[var(--q3-accent-normal)]" />
              </Button>
            </div>

            {mobileSidebarOpen && <MobileSidebar />}

            {/* Desktop sidebar (only from xl) */}
            <div className={`hidden xl:block w-64 pl-0 ${hasChapterWiseCategories ? 'xl:ml-20' : 'xl:ml-3'} shrink-0`}>
              <div className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
                <h2 className="text-lg font-bold mb-7">All Tests</h2>
                {Object.entries(
                  categories.reduce((acc, cat) => {
                    (acc[cat.parentTitle] = acc[cat.parentTitle] || []).push(cat);
                    return acc;
                  }, {}),
                ).map(([parentTitle, subCategories]) => (
                  <div key={parentTitle} className="pt-4">
                    <div className="flex items-center gap-2 px-2 mb-2">
                      <p className="text-xs text-muted-foreground font-medium tracking-wider whitespace-nowrap">
                        {parentTitle.toUpperCase()}
                      </p>
                      <div className="flex-1 border-t border-dashed border-[var(--q3-stroke-normal)]"></div>
                    </div>
                    {subCategories.map((category) => (
                      <Button
                        key={category.groupId}
                        variant={selectedCategory === category.groupId ? 'default' : 'ghost'}
                        className={cn(
                          'w-full justify-start text-left font-normal pl-4 my-1 group min-h-12 rounded-[12px]',
                          selectedCategory !== category.groupId &&
                            'transition-colors duration-200 hover:!bg-[var(--q3-surface-dimmer)]',
                        )}
                        onClick={() => {
                          setSelectedCategory(category.groupId);
                          setCurrentPage(1);
                        }}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span className="truncate">{category.shortTitle || category.title}</span>
                          <div className="flex flex-row items-center gap-2">
                            {category.isFreemium && purchasedPacks?.length === 0 && (
                              <Badge className="text-[var(--q3-neutral-default_inverted)] bg-[var(--q3-danger-normal)]">
                                Free
                              </Badge>
                            )}
                            {category?.tagText?.trim()?.length > 0 && (
                              <Badge
                                className="text-[var(--q3-neutral-default_inverted)]"
                                style={
                                  category.tagColor
                                    ? { backgroundColor: category.tagColor }
                                    : { backgroundColor: 'var(--q3-danger-normal)' }
                                }
                              >
                                {category?.tagText}
                              </Badge>
                            )}
                            <ChevronRight className="h-4 w-4 transform transition-transform duration-200 group-hover:translate-x-1" />
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 px-4 xl:px-0 max-w-[1010px] xl:pb-10 pb-20">
              {(loading || packLoading) && tests.length === 0 ? (
                <div className="pt-10">
                  <TestListingLoader />
                </div>
              ) : error ? (
                <p className="text-red-500">{error}</p>
              ) : isChapterWiseCategory ? (
                <ChapterwiseTests
                  chapterWiseTests={chapterWiseTests}
                  categories={categories}
                  totalCount={totalCount}
                  chapterFilters={chapterFilters}
                  totalPages={totalPages}
                  currentPage={currentPage}
                  availableFilters={availableFilters}
                  selectedCategoryData={selectedCategoryData}
                  handleChapterFilterChange={handleChapterFilterChange}
                  handleClearChapterFilters={handleClearChapterFilters}
                  handlePageChange={handlePageChange}
                  setSelectedCategory={handleCategoryChange}
                  setSelectedSubject={handleSubjectChange}
                  selectedSubject={selectedSubject}
                  loading={testLoading}
                  banners={banners}
                  isPackPurchased={isPackPurchased}
                />
              ) : (
                <NonChapterwiseTests
                  tests={tests}
                  isPYQCategory={isPYQCategory}
                  chapterFilters={chapterFilters}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  selectedCategoryData={selectedCategoryData}
                  handleChapterFilterChange={handleChapterFilterChange}
                  handleClearChapterFilters={handleClearChapterFilters}
                  handlePageChange={handlePageChange}
                  availableFilters={availableFilters}
                  loading={testLoading}
                  banners={banners}
                  isPackPurchased={isPackPurchased}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
